import { useCallback, useState, memo } from 'react';
import type { UIEvent, ReactNode } from 'react';
import DataTable from '../DataTable';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import Tooltip from '@/components/Tooltip';
import InformationSolid from '@simplywallst/ui-core/icons/InformationSolid';
import { useDelayedCallback } from '@/hooks/useDelayedCallback';
import type { BestValuationRatio } from '@/utilities/getBestValuationRatio';
import TableRowSkeleton from './TableRowSkeleton';
import { Row } from './Row';
import {
  Wrapper,
  ScrollPane,
  TableHead,
  NameColumnHeader,
  SnowflakeColumnHeader,
  ToolTipText,
  NoResults,
} from './styled';

const MemoRow = memo(Row);

type SortBy =
  | 'market-cap-large'
  | 'market-cap-small'
  | 'top-gainers'
  | 'biggest-losers'
  | 'growth'
  | 'dividend-yield-high';

export interface Company {
  canonicalUrl: string;
  companyId: string;
  shortDescription: string;
  mainThumb: string;
  exchangeSymbol: string;
  shortName: string;
  yearFounded: string;
  logoUrl: string;
  legalName: string;
  slug: string;
  snowflakePoints: [number, number, number, number, number];
  tickerSymbol: string;
  uniqueSymbol: string;
  return1D: number | null;
  return1DFormatted: string;
  return7D: number | null;
  return7DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  marketCap: number | null;
  marketCapFormatted: string;
  sharePrice: number | null;
  sharePriceFormatted: string;
  analystsPriceTarget: number | null;
  analystsPriceTargetFormatted: string;
  bestValuationRatio: BestValuationRatio;
  peFormatted: string;
  pbFormatted: string;
  psFormatted: string;
  analystCount: number | null;
  growthEstimate: number | null;
  growthEstimateFormatted: string;
  earningsGrowth: number | null;
  earningsGrowthFormatted: string;
  salesGrowth: number | null;
  salesGrowthFormatted: string;
  dividendYield: number | null;
  dividendYieldFormatted: string;
  industrySlug: string;
  industryLabel: string;
  isRecentSearchEntry: boolean | null;
}

export interface Props {
  companies: Company[];
  sortBy?: SortBy;
  isFetched: boolean;
  isFetching: boolean;
  industry: string | 'any';
  market: PrimaryCountryISOElseGlobal;
  onLongHover: (id: string) => void;
  hideIndustryCol?: boolean;
  noDataContent?: ReactNode;
}

interface HeaderToolTipProps {
  children: ReactNode;
}

export const HeaderToolTip = ({ children }: HeaderToolTipProps) => {
  return (
    <Tooltip title={<ToolTipText>{children}</ToolTipText>} light zIndex={99999}>
      <InformationSolid />
    </Tooltip>
  );
};

const StockTable = ({
  companies,
  industry,
  market,
  sortBy,
  isFetched,
  isFetching,
  onLongHover,
  hideIndustryCol,
  noDataContent,
}: Props) => {
  const tableColSpan = industry === 'any' ? 12 : 11;
  const [isSticky, setIsSticky] = useState(false);
  // As the tooltip exists in an <a>, mute events for touch devices to stop click through
  const muteEvent = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handlePaneScroll = useCallback(
    (e: UIEvent) => {
      const { currentTarget } = e;
      if (currentTarget.scrollLeft >= 55 && !isSticky) {
        setIsSticky(true);
      } else if (currentTarget.scrollLeft < 55 && isSticky) {
        setIsSticky(false);
      }
    },
    [isSticky]
  );

  const [delayedCallback, cancelCallback] = useDelayedCallback<string>((id) =>
    onLongHover(id)
  );

  const use1DReturn = sortBy === 'biggest-losers' || sortBy === 'top-gainers';

  return (
    <Wrapper>
      <ScrollPane onScroll={handlePaneScroll}>
        <DataTable>
          <TableHead>
            <tr>
              <SnowflakeColumnHeader></SnowflakeColumnHeader>
              <NameColumnHeader isSticky={isSticky}>Company</NameColumnHeader>
              <th>Last Price</th>
              <th data-cy-id="short-term-return-label">{`${
                use1DReturn ? '1D' : '7D'
              } Return`}</th>
              <th>1Y Return</th>
              <th>
                Market Cap
                <HeaderToolTip>
                  Market capitalization is the total value of a company,
                  representing the size of the business
                </HeaderToolTip>
              </th>
              <th>
                Analysts Target
                <HeaderToolTip>
                  The average price target provided by all the analyst
                  projections of a company's future price
                </HeaderToolTip>
              </th>
              <th>
                Valuation
                <HeaderToolTip>
                  Valuation ratios are used to determine the relative value of a
                  company compared to similar companies when applicable. <br />
                  <br />
                  Key ratios:
                  <br />
                  <strong>PE</strong> - Price to Earnings
                  <br />
                  <strong>PS</strong> - Price to Sales
                  <br />
                  <strong>PB</strong> - Price to Book
                </HeaderToolTip>
              </th>
              <th>
                Growth
                <HeaderToolTip>
                  Forecasted annual earnings and/or sales growth by analysts
                </HeaderToolTip>
              </th>
              <th>Div Yield</th>
              {industry === 'any' && !hideIndustryCol && <th>Industry</th>}
              <th />
            </tr>
          </TableHead>
          <tbody>
            {isFetching === false &&
              isFetched === true &&
              companies.length === 0 && (
                <tr>
                  <NoResults colSpan={tableColSpan} data-cy-id="no-results">
                    {noDataContent ?? 'No companies found'}
                  </NoResults>
                </tr>
              )}
            {isFetched &&
              companies.map((company, i) => {
                return (
                  <MemoRow
                    key={`${company.companyId}-stock-table`}
                    company={company}
                    industry={industry}
                    market={market}
                    shortTermReturn={
                      use1DReturn ? company.return1D : company.return7D
                    }
                    shortTermReturnFormatted={
                      use1DReturn
                        ? company.return1DFormatted
                        : company.return7DFormatted
                    }
                    hideIndustryCol={hideIndustryCol}
                    cancelCallback={cancelCallback}
                    delayedCallback={delayedCallback}
                    index={i}
                    isSticky={isSticky}
                    muteEvent={muteEvent}
                  />
                );
              })}
            {isFetching && (
              <>
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
                <TableRowSkeleton
                  isSticky={isSticky}
                  industry={industry}
                  hideIndustryCol={hideIndustryCol}
                />
              </>
            )}
          </tbody>
        </DataTable>
      </ScrollPane>
    </Wrapper>
  );
};

export default StockTable;
